
import React, { useEffect } from 'react'
import { useInsiderCampaignLoader } from 'src/hooks/useInsiderCampaignLoader'
import { useCart } from 'src/sdk/cart'
import useWindowDimensions from 'src/hooks/useWindowDimensions'
import {
  useBuyTogetherSelection,
  Buyable,
  BuyTogetherSelectionProvider,
} from 'src/contexts/InsiderBuyTogetherContext/InsiderBuyTogetherContext'
import { useProduct } from 'src/sdk/product/useProduct'
import InsiderShelfHeader from './InsiderShelfHeader'
import InsiderBuyTogetherBuyButton from './InsiderBuyButton/InsiderBuyTogetherBuyButton'
import InsiderPdpProduct from './InsiderPdpProduct'
import type { BrowserProductQueryQuery } from '@generated/graphql'
import InsiderBuyTogetherLoading from './InsiderBuyTogetherLoading'
import InsiderBuyTogetherDefaultShelf from './InsiderBuyTogetherDefaultShelf/InsiderBuyTogetherDefaultShelf'
import InsiderGraphQLProductCard from './InsiderGraphQLProductCard'

interface Props {
  campaignId?: string
  campaignIdForMobile?: string
  campaignIdForTablet?: string
  defaultCollectionId: string
  title: string
  headingTitle: string
  fontFamily: string
  productId: string
  fallbackData?: BrowserProductQueryQuery
}

const InsiderBuyTogetherContent: React.FC<Props> = ({
  campaignId,
  campaignIdForMobile,
  campaignIdForTablet,
  defaultCollectionId,
  title,
  headingTitle,
  fontFamily,
  productId,
  fallbackData,
}) => {
  const { data } = useProduct<BrowserProductQueryQuery>(productId)
  const pdp = fallbackData?.product ?? data?.product
  if (!pdp) return null

  const {
    selected: pdpSelected,
    toggleSelection,
  } = useBuyTogetherSelection()

  useEffect(() => {
    if (pdp && pdp.offers && pdp.offers.lowPrice !== undefined) {
      const buyablePdp: Buyable = {
        id: pdp.sku,
        price: pdp.offers.lowPrice,
        raw: pdp,
      }
      if (!pdpSelected[pdp.sku]) toggleSelection(buyablePdp)
    }
  }, [pdp])

  const { isMobile, isWindowBiggerThanTablet } = useWindowDimensions()
  const isTablet = !isMobile && !isWindowBiggerThanTablet
  const isDesktop = !isMobile && isWindowBiggerThanTablet

  let rawCampaignId: string | undefined
  if (isMobile && campaignIdForMobile) {
    rawCampaignId = campaignIdForMobile
  } else if (isTablet && campaignIdForTablet) {
    rawCampaignId = campaignIdForTablet
  } else if (isDesktop && campaignId) {
    rawCampaignId = campaignId
  }

  const {
    isLoading,
    hasProducts,
    products: sessionProducts,
  } = useInsiderCampaignLoader(rawCampaignId, 5000)

  const allSelected = Object.values(pdpSelected)
  const { id: cartId, items: itemsCart } = useCart()

  return (
    <section className="insider-buytogether-container">
      <div className="insider-header-wrapper">
        <InsiderShelfHeader
          title={title}
          heading={headingTitle}
          fontfamily={fontFamily}
        />
      </div>
      <div className="insider-buytogether-content">
        <div className="insider-pdp-and-shelf-bt">
          {isLoading ? (
            <InsiderBuyTogetherLoading/>
          ) : (
            <>
              <div className="insider-pdp-wrapper-bt">
                <InsiderPdpProduct
                  product={pdp}
                  isSelected={Boolean(pdpSelected[pdp?.sku])}
                  onToggle={() => {
                    const buyablePdp: Buyable = {
                      id: pdp?.sku,
                      price: pdp?.offers?.lowPrice,
                      raw: pdp,
                    }
                    toggleSelection(buyablePdp)
                  }}
                  defaultSelected={true}
                />
              </div>
              {hasProducts ? (
                <section className="insider-shelf-buy-together">
                  <div className="insider-shelf-products-bt">
                    {sessionProducts.slice(0, 2).map((p, idx) => (
                      <InsiderGraphQLProductCard
                        key={p?.item_id}
                        productId={p?.item_id}
                        index={idx}
                        defaultSelected={true}
                      />
                    ))}
                  </div>
                </section>
              ) : (
                <InsiderBuyTogetherDefaultShelf
                  collectionId={defaultCollectionId}
                  defaultSelected={true}
                />
              )}
              <div className="buy-together-footer">
                <InsiderBuyTogetherBuyButton
                  cartId={cartId}
                  itemsCart={itemsCart}
                  selectedItems={allSelected}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  )
}
export default function InsiderBuyTogether(props: Props) {
  return (
    <BuyTogetherSelectionProvider>
      <InsiderBuyTogetherContent {...props} />
    </BuyTogetherSelectionProvider>
  )
}
